// The `errors` feature's data adapters (Frontend §5.4): the same component tree on #20–#22 (clinic app) and #61–#63
// (admin app; `clinicId` narrows them to one clinic for the S-04 Errors tab).
import type { ErrorEvent, ErrorEventPage, ErrorSummary } from '@pa/api/clinic'
import { useAdminGetError, useAdminGetErrorSummary, useAdminListErrors } from '@pa/api/admin'
import { useGetError, useGetErrorSummary, useListErrors } from '@pa/api/clinic'
import { keepPreviousData } from '@tanstack/react-query'
import type { ErrorListParams } from '../model/search'

/** What the components read from a query (both apps' generated hooks satisfy it). */
export interface QueryLike<T> {
  data: T | undefined
  error: unknown
  isPending: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => Promise<unknown>
}

export interface ErrorsAdapter {
  /** S-08: events carry `clinic`; the page shows the Clinic column and filters, and the drawer links to S-04. */
  crossClinic: boolean
  useList: (params: ErrorListParams) => QueryLike<ErrorEventPage>
  useSummary: () => QueryLike<ErrorSummary>
  useOne: (errorId: string) => QueryLike<ErrorEvent>
}

const listOptions = { query: { placeholderData: keepPreviousData } }

/** C-03 / C-03a: #20 list, #21 summary, #22 one event. */
export function clinicErrorsAdapter(clinicId: string): ErrorsAdapter {
  return {
    crossClinic: false,
    useList: (params) => {
      const { clinicId: _ignored, ...rest } = params
      return useListErrors(clinicId, rest, listOptions)
    },
    useSummary: () => useGetErrorSummary(clinicId),
    useOne: (errorId) => useGetError(clinicId, errorId),
  }
}

/** S-08 (all clinics) or the S-04 Errors tab (`clinicId`): #61 list, #62 summary, #63 one event. */
export function adminErrorsAdapter(clinicId?: string): ErrorsAdapter {
  const scoped = clinicId ? { clinicId } : {}
  return {
    crossClinic: clinicId === undefined,
    useList: (params) => useAdminListErrors({ ...params, ...scoped }, listOptions),
    useSummary: () => useAdminGetErrorSummary(scoped),
    useOne: (errorId) => useAdminGetError(errorId),
  }
}
